import * as React from "react";
import { PT_ProtectiveFunction } from "../models/PT_ProtectiveFunction";
import { InputConfiguration } from "./InputConfiguration";
import { ActionConfiguration } from "./ActionConfiguration";
import { SupportConfiguration } from "./SupportConfiguration";
import { LogicConfiguration } from "./LogicConfiguration";
import DeviceInfo from "./DeviceInfo";
import { DeviceModal } from "./DeviceModal";
import { SupportModal } from "./SupportModal";
import { LogicModal } from "./LogicModal";
import { DM_PT_DataSheet } from "../models/DM_PT_DataSheet";
import { LSM_PT_LogicSolverSheet } from "../models/LSM_PT_LogicSolverSheet";
import { SS_PT_SupportSystemSheet } from "../models/SS_PT_SupportSystemSheet";
import { CcfSpecModal } from "./CcfSpecModal";
import { MooNInputConfigModal } from "./MooNInputConfigModal";
import { NoCcfModal } from "./NoCcfModal";
import { DiagramApiController } from "../models/DiagramApiController.generated";
import { GetDataSheetObject } from "../models/GetDataSheetObject";
import { DeviceData } from "../models/DeviceData";
import { GetLogicSolverParams } from "../models/GetLogicSolverParams";
import { InputRedundancyModal } from "./InputRedundancyModal";
import ConfigMenuItems from "./ConfigMenuItems";
import DeviceMenuItems from "./DeviceMenuItems";
import LoadingSpinner from "./LoadingSpinner";
import ConfigVotingInfo from "./ConfigVotingInfo";
import { ManualInputModal } from "./ManualInputModal";
import { ManualInputConfiguration } from "./ManualInputConfiguration";
import { ManualActionConfiguration } from "./ManualActionConfiguration";
import { ManualActionModal } from "./ManualActionModal";
import { ManualInputs } from "../models/ManualInputs";
import { ManualAction } from "../models/ManualAction";
import { PSF_ProtectiveFunctionComments } from "../models/PSF_ProtectiveFunctionComments";
import { getConfigRedundancy, locationType, isMooNVotingType } from "../helpers";
import { ProtectiveFunctionLocation } from "../models/protectiveFunctionLocation";
import ManualInputDeviceMenuItems from "./ManualInputDeviceMenuItems";
import ManualActionDeviceMenuItems from "./ManualActionDeviceMenuItems";
import { UpdateLogicSolverDataArgs } from "../models/UpdateLogicSolverDataArgs";
import { DeviceMenuProps } from "../models/deviceMenuProps";
import { DeleteDeviceArgs } from "../models/DeleteDeviceArgs";
import { Message } from "../models/ProtectiveFunctionWrapper";
import CalculationResultModal from "./WarningModal";
import EmptyConfiguration from "./EmptyConfiguration";
import { DiagramData } from "../models/DiagramData";
import { SilModal } from "./SilModal";
import SaveFailedModal from "./SaveFailedModal";
import panAndZoomHoc from "react-pan-and-zoom-hoc";
import { PM_ProtectiveFunction } from "../models/PM_ProtectiveFunction";
import { MasterPoolMismatchItem } from "../models/MasterPoolMismatchItem"


interface Props {
  zoom: number;
  devices: DeviceData[];
  loading: boolean;
  psfProtectiveFunctionComments: PSF_ProtectiveFunctionComments;
  redundancy: string;
  onInputRedundancyChange: (newRedundant: "NREN" | "REN") => Promise<void>;
  onUpdateCcf: (updatedPsf: PSF_ProtectiveFunctionComments) => Promise<void>;
  onUpdateDeviceSheetData: (
    deviceSheet: PT_ProtectiveFunction,
    pm: PM_ProtectiveFunction,
  ) => Promise<void>;
  onRestoreDeviceSheetData?: (
    protectiveFunctionId: number
  ) => Promise<void>;
  manualInputs: ManualInputs;
  onManualInputsSave: (manualInputs: ManualInputs) => void;
  manualAction: ManualAction;
  onManualActionSave: (manualAction: ManualAction) => void;
  setDiagramData: (diagramData: DiagramData) => void;
  copyBoundDevice: (device: DeviceData) => void;
  onPasteBoundDevice: (
    position: string,
    location: ProtectiveFunctionLocation
  ) => Promise<void>;
  pasteConfiguration: (copyLocation, pasteLocation) => void;
  deleteDevice: (device: DeviceData) => Promise<void>;
  deleteManualInput: () => void;
  deleteManualAction: () => void;
  deleteConfiguration: (configLocation: ProtectiveFunctionLocation) => void;
  handleEmptySupportDeviceLocationClick: (supportLocation: 1 | 2) => void;
  handleEmptyDeviceLocationClick: (
    position: string,
    location: ProtectiveFunctionLocation,
    voting: string
  ) => void;
  handleEmptyLogicDeviceLocationClick: () => void;
  handleUpdateLogicSolverDataClick: (
    args: UpdateLogicSolverDataArgs
  ) => Promise<boolean>;
  supportConfigPasteState: {
    ss1: boolean;
    ss2: boolean;
  };
  errorsAndWarnings?: Message[];
  showErrorsAndWarningsModal?: boolean;
  acceptCalculationResult: () => void;
  handlePanAndZoomOneUp: (scale: number) => void;
  scale: number;
  masterPoolMismatchItems?: MasterPoolMismatchItem[];
  readOnly: boolean;
}

const InteractiveDiv = panAndZoomHoc('div');

interface State {
  shownModal?:
  | "deviceModal"
  | "logicSolverModal"
  | "supportModal"
  | "configVotingModal"
  | "noCcfModal"
  | "inputRedundancyModal"
  | "menuItems"
  | "deviceMenuItems"
  | "manualInputModal"
  | "manualActionModal"
  | "manualInputDeviceMenuItems"
  | "manualActionDeviceMenuItems"
  | "showErrorsAndWarningsModal"
  | "SaveManInputsFailed"
  | "SaveManActionsFailed"
  | "MooNConfigModal";
  selectedDeviceId?: number;
  selectedLogicSolverId?: number;
  selectedSupportDeviceId?: number;
  dataSheetInfo?: DM_PT_DataSheet;
  logicSolverSheet?: LSM_PT_LogicSolverSheet;
  supportSystemSheet?: SS_PT_SupportSystemSheet;
  inputs: string;
  clickedLocation?: ProtectiveFunctionLocation;
  configMenuX: number;
  configMenuY: number;
  manualInputDeviceMenuX: number;
  manualInputDeviceMenuY: number;
  manualActionDeviceMenuX: number;
  manualActionDeviceMenuY: number;
  deviceMenuProps?: DeviceMenuProps;
  configurationCopyRegister?: ProtectiveFunctionLocation;
  x: number;
  y: number;
}

export class Diagram extends React.Component<Props, State> {
  private inputConfigSize = { height: 300, width: 350 };
  private actionConfigSize = { height: 400, width: 500 };

  constructor(props: Props) {
    super(props);
    this.state = {
      inputs: "",
      configMenuX: null,
      configMenuY: null,
      manualInputDeviceMenuX: null,
      manualInputDeviceMenuY: null,
      manualActionDeviceMenuX: null,
      manualActionDeviceMenuY: null,
      x: 0.5,
      y: 0.5
    };

    this.handleDeviceClicked = this.handleDeviceClicked.bind(this);
    this.handleConfigVotingClicked = this.handleConfigVotingClicked.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.handleDeviceRightClicked = this.handleDeviceRightClicked.bind(this);
    this.handleManualInputDeviceRightClicked = this.handleManualInputDeviceRightClicked.bind(
      this
    );
    this.handleManualActionDeviceRightClicked = this.handleManualActionDeviceRightClicked.bind(
      this
    );
  }   

  handleDeviceClicked(device: DeviceData, type: string) {
    // protectiveFunctionId: number, deviceId: string, type: string, source: string
    const getDataSheetObject = new GetDataSheetObject();
    getDataSheetObject.source = device.ptProtectiveFunction.source;
    getDataSheetObject.protectiveFunctionId =
      device.ptProtectiveFunction.protectiveFunctionID;
    getDataSheetObject.deviceId = device.ptProtectiveFunction.deviceId;
    getDataSheetObject.type = type;

    if (type === "Device") {
      DiagramApiController.bindDeviceSheet(getDataSheetObject).then(result => {
        this.setState({ dataSheetInfo: result[0] });
      });

      this.setState({
        shownModal: "deviceModal",
        selectedDeviceId: device.ptProtectiveFunction.protectiveFunctionID
      });
    } else if (type === "LSS") {
      const getLogicSolverParams = new GetLogicSolverParams();
      getLogicSolverParams.deviceId = device.ptProtectiveFunction.deviceId;
      getLogicSolverParams.protectiveFunctionIdLogic =
        device.pmProtectiveFunction.protectiveFunctionID_FK;
      getLogicSolverParams.logicSolverSheetId =
        device.pmProtectiveFunction.pmProtectiveFunctionID;

      DiagramApiController.bindLogicSolver(getLogicSolverParams).then(resp =>
        console.log(resp)
      );

      this.setState({
        shownModal: "logicSolverModal",
        selectedLogicSolverId: device.ptProtectiveFunction.protectiveFunctionID
      });
    } else if (type === "SSS") {
      DiagramApiController.bindSupportSystem(getDataSheetObject).then(resp => {
        this.setState({
          supportSystemSheet: resp[0]
        });
      });

      this.setState({
        shownModal: "supportModal",
        selectedSupportDeviceId:
          device.ptProtectiveFunction.protectiveFunctionID
      });
    } else if (type === "ManualInputDevice") {
      DiagramApiController.bindSupportSystem(getDataSheetObject).then(resp => {
        this.setState({
          supportSystemSheet: resp[0]
        });
      });

      this.setState({
        shownModal: "manualInputModal",
        selectedSupportDeviceId:
          device.ptProtectiveFunction.protectiveFunctionID
      });
    } else if (type === "ManualActionDevice") {
      DiagramApiController.bindSupportSystem(getDataSheetObject).then(resp => {
        this.setState({
          supportSystemSheet: resp[0]
        });
      });

      this.setState({
        shownModal: "manualActionModal",
        selectedSupportDeviceId:
          device.ptProtectiveFunction.protectiveFunctionID
      });
    }
  }

  handleEmptyConfigurationRightClicked = (
    e: React.MouseEvent,
    configLocation: ProtectiveFunctionLocation
  ) => {
    e.preventDefault();

    if (
      locationType(this.state.configurationCopyRegister) !==
      locationType(configLocation)
    ) {
      return;
    }

    const pasteConfigAction = () => {
      this.props.pasteConfiguration(
        this.state.configurationCopyRegister,
        configLocation
      );
      this.closeModal();
    };

    const actions = this.props.readOnly
      ? []
      :[{ name: "Paste", do: () => pasteConfigAction() }];

    this.setState({
      deviceMenuProps: {
        top: e.clientY,
        left: e.clientX,
        actions: actions
      },
      shownModal: "deviceMenuItems"
    });
  };

  // Right-click device
  handleDeviceRightClicked(e: React.MouseEvent, device: DeviceData) {
    e.preventDefault();

    const copyDeviceAction = () => {
      this.props.copyBoundDevice(device);
      this.closeModal();
    };

    const deleteDeviceAction = () => {
      this.props.deleteDevice(device).then(() => this.closeModal());
    };

    let actions = [];
    if (!this.props.readOnly) {
      actions = ["LSO1", "SSO1", "SSO2"].indexOf(device.ptProtectiveFunction.inputs) > -1
          ? [{ name: "Delete", do: () => deleteDeviceAction() }]
          : [
            { name: "Copy", do: () => copyDeviceAction() },
            { name: "Delete", do: () => deleteDeviceAction() }
          ];
    }

    this.setState({
      deviceMenuProps: {
        top: e.clientY,
        left: e.clientX,
        actions
      },
      shownModal: "deviceMenuItems"
    });
  }

  handleEmptyDeviceLocationRightClick = (
    e: React.MouseEvent,
    deviceLocation: string,
    configLocation: ProtectiveFunctionLocation
  ) => {
    e.preventDefault();
    const pasteAction = () => {
      this.props
        .onPasteBoundDevice(deviceLocation, configLocation)
        .then(() => this.closeModal());
    };
    const actions = this.props.readOnly
      ? []
      : [{ name: "Paste", do: () => pasteAction() }];

    this.setState({
      deviceMenuProps: {
        top: e.clientY,
        left: e.clientX,
        actions
      },
      shownModal: "deviceMenuItems"
    });
  };

  handleConfigurationRightClicked = (
    e: React.MouseEvent,
    location: ProtectiveFunctionLocation
  ) => {
    e.preventDefault();

    const copyAction = () => {
      this.setState({ configurationCopyRegister: location });
      this.closeModal();
    };
    const deleteAction = () => {
      console.log("deleting");
      this.props.deleteConfiguration(location);
      this.closeModal();
    };

    const actions = [];
    if (location === "SSO1" || location === "SSO2") {
      actions.push({ name: "Delete", do: () => deleteAction() });
    } else {
      actions.push({ name: "Copy", do: () => copyAction() });
      actions.push({ name: "Delete", do: () => deleteAction() });
    }

    this.setState({
      deviceMenuProps: {
        top: e.clientY,
        left: e.clientX,
        actions: actions
      },
      shownModal: "deviceMenuItems"
    });
  };

  // Configuration Voting Clicked
  handleConfigVotingClicked(
    location: ProtectiveFunctionLocation,
    voting: string
  ) {
    if (this.props.readOnly) { return; }
    if (isMooNVotingType(voting)) {
        this.setState({
          shownModal: "MooNConfigModal",
          clickedLocation: location
        });
    } else {
      const redundancy = getConfigRedundancy(voting);
      if (redundancy === "REN") {
        this.setState({
          shownModal: "configVotingModal",
          clickedLocation: location
        });
      } else {
        this.setState({
          shownModal: "noCcfModal",
          clickedLocation: location
        });
      }
    }
  }

  handleinputRedundancyClicked() {
    if (this.props.readOnly) { return; }
    this.setState({
      shownModal: "inputRedundancyModal"
    });
  }

  closeModal() {
    this.setState({
      shownModal: null,
      selectedDeviceId: null,
      selectedLogicSolverId: null,
      selectedSupportDeviceId: null,
      dataSheetInfo: null,
      logicSolverSheet: null,
      supportSystemSheet: null,
      inputs: ""
    });
  }

  configHasDevices(config: ProtectiveFunctionLocation) {
    const deviceExists =
      this.props.devices.filter(
        d => d.ptProtectiveFunction.inputs.trim() == config
      ).length >= 1;
    const supportConfigWasPasted = false;

    return deviceExists || supportConfigWasPasted;
  }

  // Right-click Manual Input Device
  handleManualInputDeviceRightClicked(e: React.MouseEvent) {
    if (this.props.readOnly) { return; }

    e.preventDefault();
    console.log(e.clientX, e.clientY);
    this.setState({
      manualInputDeviceMenuX: e.clientX,
      manualInputDeviceMenuY: e.clientY,
      shownModal: "manualInputDeviceMenuItems"
    });
  }

  // Right-click Manual Action Device
  handleManualActionDeviceRightClicked(e: React.MouseEvent) {
    if (this.props.readOnly) { return; }

    e.preventDefault();
    console.log(e.clientX, e.clientY);
    this.setState({
      manualActionDeviceMenuX: e.clientX,
      manualActionDeviceMenuY: e.clientY,
      shownModal: "manualActionDeviceMenuItems"
    });
  }

  // Save Manual Inputs Failed --> Save Failed Modal pops up
  saveManualInputsFailed = (e: React.MouseEvent) => {
    e.preventDefault();
    this.setState({
      shownModal: "SaveManInputsFailed"
    });
  };

  // Save Manual Actions Failed --> Save Failed Modal pops up
  saveManualActionsFailed = (e: React.MouseEvent) => {
    e.preventDefault();
    this.setState({
      shownModal: "SaveManActionsFailed"
    });
  };

  //handlePanAndZoom = (x, y, scale) => {
  //  this.setState({ x, y, scale });
  //}

  handlePanAndZoomOneUp = (x, y, scale) => {
    this.setState({ x, y });
    this.props.handlePanAndZoomOneUp(scale);
  }

  handlePanMove = (x, y) => {
    this.setState({ x, y })
  }


  handleZoomEnd = () => console.log('Zoom has ended.');

  transformPoint({ x, y }) {
    return {
      x: 0.5 + this.props.scale * (x - this.state.x),
      y: 0.5 + this.props.scale * (y - this.state.y)
    };
  }

  rendered = 0;

  render() {
    const zoom = this.props.zoom;
    const horizontalLineStyle: React.CSSProperties = {
      position: "absolute",
      height: `${zoom * 300}px`,
      pointerEvents: "none"
    };
    const verticalLineStyle: React.CSSProperties = {
      position: "absolute",
      width: `${zoom * 350}px`,
      pointerEvents: "none"
    };
    const redundancy = this.props.redundancy;

    const redundancyImg =
      this.props.redundancy && this.props.redundancy.trim() === "REN"
        ? "/HtmlImages/Image/ren.png"
        : "/HTMLImages/Image/nren.png";

    let scale = this.props.scale;
    let x = this.state.x;
    let y = this.state.y;
    if (!this.props.loading) {
      this.rendered++;
    }


    return (
      <InteractiveDiv
        className="diagram-wrapper"
        x={x}
        y={y}
        scale={scale}
        scaleFactor={Math.sqrt(2)}
        minScale={0.1}
        maxScale={5}
        onPanAndZoom={this.handlePanAndZoomOneUp}
        ignorePanOutside
        onPanMove={this.handlePanMove}
        onZoomEnd={this.handleZoomEnd}
      >
        <div id="isLoaded" data-render={this.rendered} style={{ display: 'none' }}></div>    
      <div className="main-diagram" style={{ position: "relative", zoom: scale }}>
        {/*Loading Spinner*/}
        {false ? <LoadingSpinner /> : null}
        {/*Device Menu Items*/}
        {this.state.deviceMenuProps != null &&
          this.state.shownModal === "deviceMenuItems" ? (
            <div
              id="deviceMenuItems"
              style={{
                position: "fixed",
                zIndex: 1000,
                top: `${this.state.deviceMenuProps.top}px`,
                left: `${this.state.deviceMenuProps.left}px`,
                width: `${140 * zoom}px`,
                height: `${120 * zoom}px`
              }}
            >
              <DeviceMenuItems
                closeModal={this.closeModal}
                deviceMenuProps={this.state.deviceMenuProps}
                onDeleteClicked={this.props.deleteDevice}
                onCopyClicked={this.props.copyBoundDevice}
              />
            </div>
          ) : null}
        {/*Manual Input Device Menu Items*/}
        {this.state.manualInputDeviceMenuX != null &&
          this.state.manualInputDeviceMenuY != null &&
          this.state.shownModal === "manualInputDeviceMenuItems" ? (
            <div
              id="inputConfigMenuItems"
              style={{
                position: "fixed",
                zIndex: 1000,
                top: `${this.state.manualInputDeviceMenuY}px`,
                left: `${this.state.manualInputDeviceMenuX}px`,
                width: `${140 * zoom}px`,
                height: `${120 * zoom}px`
              }}
            >
              <ManualInputDeviceMenuItems
                closeModal={this.closeModal}
                manualInputDeviceMenuX={this.state.manualInputDeviceMenuX}
                manualInputDeviceMenuY={this.state.manualInputDeviceMenuY}
                deleteManualInput={this.props.deleteManualInput}
              />
            </div>
          ) : null}
        {/*Manual Action Device Menu Items*/}
        {this.state.manualActionDeviceMenuX != null &&
          this.state.manualActionDeviceMenuY != null &&
          this.state.shownModal === "manualActionDeviceMenuItems" ? (
            <div
              id="inputConfigMenuItems"
              style={{
                position: "fixed",
                zIndex: 1000,
                top: `${this.state.manualActionDeviceMenuY}px`,
                left: `${this.state.manualActionDeviceMenuX}px`,
                width: `${140 * zoom}px`,
                height: `${120 * zoom}px`
              }}
            >
              <ManualActionDeviceMenuItems
                closeModal={this.closeModal}
                manualActionDeviceMenuX={this.state.manualActionDeviceMenuX}
                manualActionDeviceMenuY={this.state.manualActionDeviceMenuY}
                deleteManualAction={this.props.deleteManualAction}
              />
            </div>
          ) : null}

        {/*Input 1*/}
        {this.props.psfProtectiveFunctionComments.input1Config ? (
          <div
            id="div1"
            style={{
              zIndex: 2,
              position: "absolute",
              top: `${41 * zoom}px`,
              left: `${908 * zoom}px`,
              width: `${350 * zoom}px`,
              height: `${300 * zoom}px`,
              overflow: "visible",
              pointerEvents: "none"
            }}
          >
            <InputConfiguration
              zoom={this.props.zoom}
              location={"INP1"}
              voting={this.props.psfProtectiveFunctionComments.input1Config}
              devices={this.props.devices.filter(device =>
                device.ptProtectiveFunction.inputs.trim().startsWith("INP1")
              )}
              handleDeviceClicked={this.handleDeviceClicked}
              onConfigVotingClicked={this.handleConfigVotingClicked}
              handleConfigurationClicked={this.handleConfigurationRightClicked}
              handleDeviceRightClicked={this.handleDeviceRightClicked}
              handleEmptyDeviceLocationClick={
                this.props.handleEmptyDeviceLocationClick
              }
              onEmptyDeviceLocationRightClick={
                this.handleEmptyDeviceLocationRightClick
              }
              errorsAndWarnings={this.props.errorsAndWarnings}
              masterPoolMismatchItems={this.props.masterPoolMismatchItems}
              inputConfigurationName={"Input1"}
            />
            <ConfigVotingInfo
                x={`${zoom * 24}px`}
                y={`${zoom * 232}px`}
                fontSize={`${zoom * 0.9}em`}
                device={
                  this.props.devices.filter(
                    device =>
                      device.ptProtectiveFunction.inputs.trim() === "INP1T"
                  )[0]
                }
                ccf={this.props.psfProtectiveFunctionComments.input1CCF}
            />
            <img
              style={{
                zIndex: -1,
                top: `${zoom * 162}px`,
                left: `${-19 * zoom}px`,
                height: `${zoom * 295}px`,
                ...verticalLineStyle
              }}
              src="/HTMLImages/Elements/Vertical-small1.png"
            />
            <img
              style={{
                zIndex: -1,
                top: `${zoom * 162}px`,
                left: `${151 * zoom}px`,
                width: `${zoom * 177}px`,
                ...horizontalLineStyle
              }}
              src="/HTMLImages/Elements/Horizontal-big1.png"
            />
          </div>
        ) : (
            <EmptyConfiguration
              location="INP1"
              position={{ top: 41, left: 905 }}
              size={this.inputConfigSize}
              onRightClick={this.handleEmptyConfigurationRightClicked}
              zoom={this.props.zoom}
            />
          )}

        {/*Input 2*/}
        {this.props.psfProtectiveFunctionComments.input2Config ? (
          <div
            id="div2"
            style={{
              zIndex: 2,
              position: "absolute",
              top: `${41 * zoom}px`,
              left: `${504 * zoom}px`,
              width: `${350 * zoom}px`,
              height: `${300 * zoom}px`
            }}
          >
            <InputConfiguration
              zoom={this.props.zoom}
              location={"INP2"}
              voting={this.props.psfProtectiveFunctionComments.input2Config}
              devices={this.props.devices.filter(device =>
                device.ptProtectiveFunction.inputs.trim().startsWith("INP2")
              )}
              handleDeviceClicked={this.handleDeviceClicked}
              onConfigVotingClicked={this.handleConfigVotingClicked}
              handleConfigurationClicked={this.handleConfigurationRightClicked}
              handleDeviceRightClicked={this.handleDeviceRightClicked}
              handleEmptyDeviceLocationClick={
                this.props.handleEmptyDeviceLocationClick
              }
              onEmptyDeviceLocationRightClick={
                this.handleEmptyDeviceLocationRightClick
              }
              errorsAndWarnings={this.props.errorsAndWarnings}
              masterPoolMismatchItems={this.props.masterPoolMismatchItems}
              inputConfigurationName={"Input2"}
            />
            <ConfigVotingInfo
              x={`${zoom * 17}px`}
              y={`${zoom * 229}px`}
              fontSize={`${zoom * 0.9}em`}
              device={
                this.props.devices.filter(
                  device =>
                    device.ptProtectiveFunction.inputs.trim() === "INP2T"
                )[0]
              }
              ccf={this.props.psfProtectiveFunctionComments.input2CCF}
            />
            <img
              style={{
                zIndex: -1,
                top: `${zoom * 179}px`,
                left: `${150 * zoom}px`,
                ...horizontalLineStyle
              }}
              src="/HTMLImages/Elements/Horizontal-big.png"
            />
            <img
              style={{
                zIndex: -1,
                top: `${zoom * 179}px`,
                left: `${432 * zoom}px`,
                ...horizontalLineStyle
              }}
              src="/HTMLImages/Elements/Horizontal-big.png"
            />
            <img
              style={{
                zIndex: -1,
                top: `${zoom * 173}px`,
                left: `${4 * zoom}px`,
                ...horizontalLineStyle
              }}
              src="/HTMLImages/Elements/Vertical-small2.png"
            />
          </div>
        ) : (
            <EmptyConfiguration
              location="INP2"
              position={{ top: 41, left: 500 }}
              size={this.inputConfigSize}
              onRightClick={this.handleEmptyConfigurationRightClicked}
              zoom={this.props.zoom}
            />
          )}

        {/*Input 3*/}
        {this.props.psfProtectiveFunctionComments.input3Config ? (
          <div
            id="div3"
            style={{
              zIndex: 2,
              position: "absolute",
              top: `${36 * zoom}px`,
              left: `${86 * zoom}px`,
              width: `${350 * zoom}px`,
              height: `${300 * zoom}px`,
              overflow: "visible",
              pointerEvents: "none"
            }}
          >
            <InputConfiguration
              zoom={this.props.zoom}
              location={"INP3"}
              voting={this.props.psfProtectiveFunctionComments.input3Config}
              devices={this.props.devices.filter(device =>
                device.ptProtectiveFunction.inputs.trim().startsWith("INP3")
              )}
              handleDeviceClicked={this.handleDeviceClicked}
              onConfigVotingClicked={this.handleConfigVotingClicked}
              handleConfigurationClicked={this.handleConfigurationRightClicked}
              handleDeviceRightClicked={this.handleDeviceRightClicked}
              handleEmptyDeviceLocationClick={
                this.props.handleEmptyDeviceLocationClick
              }
              onEmptyDeviceLocationRightClick={
                this.handleEmptyDeviceLocationRightClick
              }
              errorsAndWarnings={this.props.errorsAndWarnings}
              masterPoolMismatchItems={this.props.masterPoolMismatchItems}
              inputConfigurationName={"Input3"}
            />
            <ConfigVotingInfo
              x={`${zoom * 21}px`}
              y={`${zoom * 234}px`}
              fontSize={`${zoom * 0.9}em`}
              device={
                this.props.devices.filter(
                  device =>
                    device.ptProtectiveFunction.inputs.trim() === "INP3T"
                )[0]
                }
                ccf={this.props.psfProtectiveFunctionComments.input3CCF}
            />
            <img
              style={{
                zIndex: -1,
                top: `${zoom * 204}px`,
                left: `${zoom * 851}px`,
                ...horizontalLineStyle
              }}
              src="/HTMLImages/Elements/Horizontal-big.png"
            />
            <img
              style={{
                zIndex: -1,
                top: `${zoom * 204}px`,
                left: `${zoom * 569}px`,
                ...horizontalLineStyle
              }}
              src="/HTMLImages/Elements/Horizontal-big.png"
            />
            <img
              style={{
                zIndex: -1,
                top: `${zoom * 204}px`,
                left: `${zoom * 287}px`,
                ...horizontalLineStyle
              }}
              src="/HTMLImages/Elements/Horizontal-big.png"
            />
            <img
              style={{
                zIndex: -1,
                top: `${zoom * 204}px`,
                left: `${zoom * 149}px`,
                ...horizontalLineStyle
              }}
              src="/HTMLImages/Elements/Horizontal-big1.png"
            />
            <img
              style={{
                zIndex: -1,
                top: `${zoom * 170}px`,
                left: `${zoom * -21}px`,
                ...verticalLineStyle
              }}
              src="/HTMLImages/Elements/Vertical-small2.png"
            />
            <img
              style={{
                zIndex: -1,
                top: `${zoom * 154}px`,
                left: `${zoom * -21}px`,
                ...verticalLineStyle
              }}
              src="/HTMLImages/Elements/Vertical-small2.png"
            />
          </div>
        ) : (
            <EmptyConfiguration
              location="INP3"
              position={{ top: 36, left: 86 }}
              size={this.inputConfigSize}
              onRightClick={this.handleEmptyConfigurationRightClicked}
              zoom={this.props.zoom}
            />
          )}

        {/*Input 4*/}
        {this.props.psfProtectiveFunctionComments.input4Config ? (
          <div
            id="div4"
            style={{
              zIndex: 2,
              position: "absolute",
              top: `${451 * zoom}px`,
              left: `${814 * zoom}px`,
              width: `${350 * zoom}px`,
              height: `${300 * zoom}px`,
              overflow: "visible",
              pointerEvents: "none"
            }}
          >
            <InputConfiguration
              zoom={this.props.zoom}
              location={"INP4"}
              voting={this.props.psfProtectiveFunctionComments.input4Config}
              devices={this.props.devices.filter(device =>
                device.ptProtectiveFunction.inputs.trim().startsWith("INP4")
              )}
              handleDeviceClicked={this.handleDeviceClicked}
              onConfigVotingClicked={this.handleConfigVotingClicked}
              handleConfigurationClicked={this.handleConfigurationRightClicked}
              handleDeviceRightClicked={this.handleDeviceRightClicked}
              handleEmptyDeviceLocationClick={
                this.props.handleEmptyDeviceLocationClick
              }
              onEmptyDeviceLocationRightClick={
                this.handleEmptyDeviceLocationRightClick
              }
              errorsAndWarnings={this.props.errorsAndWarnings}
              masterPoolMismatchItems={this.props.masterPoolMismatchItems}
              inputConfigurationName={"Input4"}
            />
            <ConfigVotingInfo
              x={`${zoom * 20}px`}
              y={`${zoom * 220}px`}
              fontSize={`${zoom * 0.9}em`}
              device={
                this.props.devices.filter(
                  device =>
                    device.ptProtectiveFunction.inputs.trim() === "INP4T"
                )[0]
                }
                ccf={this.props.psfProtectiveFunctionComments.input4CCF}
            />
            <img
              style={{
                zIndex: -1,
                top: `${zoom * -193}px`,
                left: `${zoom * 232}px`,
                width: `${zoom * 284}px`,
                ...horizontalLineStyle
              }}
              src="/HTMLImages/Elements/Horizontal-small1.png"
            />
            <img
              style={{
                zIndex: -1,
                top: `${zoom * -45}px`,
                left: `${zoom * 163}px`,
                height: `${zoom * 362}px`,
                ...verticalLineStyle
              }}
              src="/HTMLImages/Elements/Vertical-big.png"
            />
            <img
              style={{
                zIndex: -1,
                top: `${zoom * 166}px`,
                left: `${zoom * 153}px`,
                width: `${zoom * 182}px`,
                ...horizontalLineStyle
              }}
              src="/HTMLImages/Elements/Horizontal-big1.png"
            />
            <img
              style={{
                zIndex: -1,
                top: `${zoom * 138}px`,
                left: `${zoom * -19}px`,
                ...verticalLineStyle
              }}
              src="/HTMLImages/Elements/Vertical-small1.png"
            />
          </div>
        ) : (
            <EmptyConfiguration
              location="INP4"
              position={{ top: 451, left: 814 }}
              size={this.inputConfigSize}
              onRightClick={this.handleEmptyConfigurationRightClicked}
              zoom={this.props.zoom}
            />
          )}

        {/*Input 5*/}
        {this.props.psfProtectiveFunctionComments.input5Config ? (
          <div
            id="div5"
            style={{
              zIndex: 2,
              position: "absolute",
              top: `${448 * zoom}px`,
              left: `${304 * zoom}px`,
              width: `${350 * zoom}px`,
              height: `${300 * zoom}px`,
              overflow: "visible",
              pointerEvents: "none"
            }}
          >
            <InputConfiguration
              zoom={this.props.zoom}
              location={"INP5"}
              voting={this.props.psfProtectiveFunctionComments.input5Config}
              devices={this.props.devices.filter(device =>
                device.ptProtectiveFunction.inputs.trim().startsWith("INP5")
              )}
              handleDeviceClicked={this.handleDeviceClicked}
              onConfigVotingClicked={this.handleConfigVotingClicked}
              handleConfigurationClicked={this.handleConfigurationRightClicked}
              handleDeviceRightClicked={this.handleDeviceRightClicked}
              handleEmptyDeviceLocationClick={
                this.props.handleEmptyDeviceLocationClick
              }
              onEmptyDeviceLocationRightClick={
                this.handleEmptyDeviceLocationRightClick
              }
              errorsAndWarnings={this.props.errorsAndWarnings}
              masterPoolMismatchItems={this.props.masterPoolMismatchItems}
              inputConfigurationName={"Input5"}
            />
            <ConfigVotingInfo
              x={`${zoom * 15}px`}
              y={`${zoom * 216}px`}
              fontSize={`${zoom * 0.9}em`}
              device={
                this.props.devices.filter(
                  device =>
                    device.ptProtectiveFunction.inputs.trim() === "INP5T"
                )[0]
                }
                ccf={this.props.psfProtectiveFunctionComments.input5CCF}
            />
            <img
              style={{
                zIndex: -1,
                top: `${zoom * -174}px`,
                left: `${zoom * 750}px`,
                ...horizontalLineStyle
              }}
              src="/HTMLImages/Elements/Horizontal-small2.png"
            />
            <img
              style={{
                zIndex: -1,
                top: `${zoom * 186}px`,
                left: `${zoom * 145}px`,
                ...horizontalLineStyle
              }}
              src="/HTMLImages/Elements/Horizontal-big.png"
              />
              <img
                style={{
                  zIndex: -1,
                  top: `${zoom * 186}px`,
                  left: `${zoom * 430}px`,
                  ...horizontalLineStyle
                }}
                src="/HTMLImages/Elements/Horizontal-big.png"
              />
              <img
                style={{
                  zIndex: -1,
                  top: `${zoom * 186}px`,
                  left: `${zoom * 565}px`,
                  ...horizontalLineStyle
                }}
                src="/HTMLImages/Elements/Horizontal-big.png"
              />
            <img
              style={{
                zIndex: -1,
                top: `${zoom * 161}px`,
                left: `${zoom * -24}px`,
                height: `${zoom * 329}px`,
                ...verticalLineStyle
              }}
              src="/HTMLImages/Elements/Vertical-small2.png"
            />
            <img
              style={{
                zIndex: -1,
                top: `${zoom * -26}px`,
                left: `${zoom * 692}px`,
                height: `${zoom * 362}px`,
                ...verticalLineStyle
              }}
              src="/HTMLImages/Elements/Vertical-big.png"
            />
          </div>
        ) : (
            <EmptyConfiguration
              location="INP5"
              position={{ top: 448, left: 304 }}
              size={this.inputConfigSize}
              onRightClick={this.handleEmptyConfigurationRightClicked}
              zoom={this.props.zoom}
            />
          )}

        {/*Logic solver*/}
        {this.props.devices.filter(
          device => device.ptProtectiveFunction.inputs.trim() === "LSO1"
        ) !== null && (
            <div
              id="div6"
              style={{
                position: "absolute",
                top: `${250 * zoom}px`,
                left: `${1200 * zoom}px`,
                width: `${260 * zoom}px`,
                height: `${260 * zoom}px`
              }}
            >
              {/*The Sammich - you know*/}
              <div
                style={{
                  position: "absolute",
                  top: `${34 * zoom}px`,
                  left: `${35 * zoom}px`,
                  width: `${230 * zoom}px`,
                  height: `${260 * zoom}px`,
                  backgroundColor: "white",
                  zIndex: 5,
                  pointerEvents: "none"
                }}
              ></div>
              <img
                src={redundancyImg}
                style={{
                  zIndex: 5500,
                  position: "absolute",
                  top: `${120 * zoom}px`,
                  left: `${42 * zoom}px`,
                  width: `${zoom * 17}px`
                }}
                onClick={() => this.handleinputRedundancyClicked() }
              />
              <img
                src="/HTMLImages/Image/template.png"
                style={{ zIndex: 6, position: "absolute", width: `${300 * zoom}px` }}
              />
              <LogicConfiguration
                zoom={this.props.zoom}
                devices={this.props.devices.filter(
                  device => device.ptProtectiveFunction.inputs.trim() === "LSO1"
                )}
                handleDeviceClicked={this.handleDeviceClicked}
                handleDeviceRightClicked={this.handleDeviceRightClicked}
                handleEmptyLogicDeviceLocationClick={
                  this.props.handleEmptyLogicDeviceLocationClick
                }
                errorsAndWarnings={this.props.errorsAndWarnings}
                masterPoolMismatchItems={this.props.masterPoolMismatchItems}
              />
            </div>
          )}

        {/*Action 1*/}
        {this.props.psfProtectiveFunctionComments.action1Config ? (
          <div
            id="diva1"
            style={{
              zIndex: 2,
              position: "absolute",
              top: `${613 * zoom}px`,
              left: `${1616 * zoom}px`,
              width: `${500 * zoom}px`,
              height: `${400 * zoom}px`,
              overflow: "visible",
              pointerEvents: "none"
            }}
          >
            <ActionConfiguration
              zoom={this.props.zoom}
              location={"A1O1"}
              voting={this.props.psfProtectiveFunctionComments.action1Config}
              devices={this.props.devices.filter(device =>
                device.ptProtectiveFunction.inputs.trim().startsWith("A1O1")
              )}
              handleDeviceClicked={this.handleDeviceClicked}
              onConfigVotingClicked={this.handleConfigVotingClicked}
              handleConfigurationClicked={this.handleConfigurationRightClicked}
              handleDeviceRightClicked={this.handleDeviceRightClicked}
              handleEmptyDeviceLocationClick={
                this.props.handleEmptyDeviceLocationClick
              }
              onEmptyDeviceLocationRightClick={
                this.handleEmptyDeviceLocationRightClick
              }
              errorsAndWarnings={this.props.errorsAndWarnings}
              masterPoolMismatchItems={this.props.masterPoolMismatchItems}
              actionConfigurationName={"Action1"}
            />
            <ConfigVotingInfo
              x={`${zoom * -41}px`}
              y={`${zoom * 120}px`}
              fontSize={`${zoom * 0.9}em`}
              device={
                this.props.devices.filter(
                  device =>
                    device.ptProtectiveFunction.inputs.trim() === "A1O1T"
                )[0]
                }
                ccf={this.props.psfProtectiveFunctionComments.action1CCF}
            />
            <img
              style={{
                zIndex: -1,
                top: `${zoom * -335}px`,
                left: `${zoom * -265}px`,
                width: `${zoom * 318}px`,
                ...horizontalLineStyle
              }}
              src="/HTMLImages/Elements/Horizontal-small1.png"
            />
              <img
                style={{
                  zIndex: -1,
                  top: `${zoom * -335}px`,
                  left: `${zoom * -300}px`,
                  width: `${zoom * 318}px`,
                  ...horizontalLineStyle
                }}
                src="/HTMLImages/Elements/Horizontal-small1.png"
              />
            <img
              style={{
                zIndex: -1,
                top: `${zoom * -188}px`,
                left: `${zoom * -226}px`,
                height: `${zoom * 307}px`,
                ...verticalLineStyle
              }}
              src="/HTMLImages/Elements/Vertical-big.png"
            />
            <img
              style={{
                zIndex: -1,
                top: `${zoom * -31}px`,
                left: `${zoom * -230}px`,
                width: `${zoom * 493}px`,
                ...horizontalLineStyle
              }}
              src="/HTMLImages/Elements/Horizontal-small1.png"
              />
              
          </div>
        ) : (
            <EmptyConfiguration
              location="A1O1"
              position={{ top: 613, left: 1616 }}
              size={this.actionConfigSize}
              onRightClick={this.handleEmptyConfigurationRightClicked}
              zoom={this.props.zoom}
            />
          )}

        {/*Action 2*/}
        {this.props.psfProtectiveFunctionComments.action2Config ? (
          <div
            id="diva2"
            style={{
              zIndex: 3,
              position: "absolute",
              top: `${600 * zoom}px`,
              left: `${2164 * zoom}px`,
              width: `${500 * zoom}px`,
              height: `${400 * zoom}px`,
              overflow: "visible",
              pointerEvents: "none"
            }}
          >
            <ActionConfiguration
              zoom={this.props.zoom}
              location={"A1O2"}
              voting={this.props.psfProtectiveFunctionComments.action2Config}
              devices={this.props.devices.filter(device =>
                device.ptProtectiveFunction.inputs.trim().startsWith("A1O2")
              )}
              handleDeviceClicked={this.handleDeviceClicked}
              onConfigVotingClicked={this.handleConfigVotingClicked}
              handleConfigurationClicked={this.handleConfigurationRightClicked}
              handleDeviceRightClicked={this.handleDeviceRightClicked}
              handleEmptyDeviceLocationClick={
                this.props.handleEmptyDeviceLocationClick
              }
              onEmptyDeviceLocationRightClick={
                this.handleEmptyDeviceLocationRightClick
              }
              errorsAndWarnings={this.props.errorsAndWarnings}
              masterPoolMismatchItems={this.props.masterPoolMismatchItems}
              actionConfigurationName={"Action2"}
            />
            <ConfigVotingInfo
              x={`${zoom * -23}px`}
              y={`${zoom * 123}px`}
              fontSize={`${zoom * 0.9}em`}
              device={
                this.props.devices.filter(
                  device =>
                    device.ptProtectiveFunction.inputs.trim() === "A1O2T"
                )[0]
                }
                ccf={this.props.psfProtectiveFunctionComments.action2CCF}
              />
              <img
                style={{
                  zIndex: -1,
                  top: `${zoom * -339}px`,
                  left: `${zoom * -303}px`,
                  width: `${zoom * 300}px`,
                  ...horizontalLineStyle
                }}
                src="/HTMLImages/Elements/Horizontal-big1.png"
              />
            <img
              style={{
                zIndex: -1,
                top: `${zoom * -339}px`,
                left: `${zoom * -700}px`,
                width: `${zoom * 700}px`,
                ...horizontalLineStyle
              }}
              src="/HTMLImages/Elements/Horizontal-big1.png"
            />
            <img
              style={{
                zIndex: -1,
                top: `${zoom * -192}px`,
                left: `${zoom * -172}px`,
                height: `${zoom * 312}px`,
                ...verticalLineStyle
              }}
              src="/HTMLImages/Elements/Vertical-big.png"
            />
            <img
              style={{
                zIndex: -1,
                top: `${zoom * -30}px`,
                left: `${zoom * -175}px`,
                width: `${zoom * 458}px`,
                ...horizontalLineStyle
              }}
              src="/HTMLImages/Elements/Horizontal-small2.png"
              />
              <img
                style={{
                  zIndex: -1,
                  top: `${zoom * -339}px`,
                  left: `${zoom * -850}px`,
                  width: `${zoom * 318}px`,
                  ...horizontalLineStyle
                }}
                src="/HTMLImages/Elements/Horizontal-small1.png"
              />
          </div>
        ) : (
            <EmptyConfiguration
              location="A1O2"
              position={{ top: 600, left: 2164 }}
              size={this.actionConfigSize}
              onRightClick={this.handleEmptyConfigurationRightClicked}
              zoom={this.props.zoom}
            />
          )}

        {/*Action 3*/}
        {this.props.psfProtectiveFunctionComments.action3Config ? (
          <div
            id="diva3"
            style={{
              zIndex: 2,
              position: "absolute",
              top: `${-63 * zoom}px`,
              left: `${1616 * zoom}px`,
              width: `${500 * zoom}px`,
              height: `${400 * zoom}px`,
              overflow: "visible",
              pointerEvents: "none"
            }}
          >
            <ActionConfiguration
              zoom={this.props.zoom}
              location={"A1O3"}
              voting={this.props.psfProtectiveFunctionComments.action3Config}
              devices={this.props.devices.filter(device =>
                device.ptProtectiveFunction.inputs.trim().startsWith("A1O3")
              )}
              handleDeviceClicked={this.handleDeviceClicked}
              onConfigVotingClicked={this.handleConfigVotingClicked}
              handleConfigurationClicked={this.handleConfigurationRightClicked}
              handleDeviceRightClicked={this.handleDeviceRightClicked}
              handleEmptyDeviceLocationClick={
                this.props.handleEmptyDeviceLocationClick
              }
              onEmptyDeviceLocationRightClick={
                this.handleEmptyDeviceLocationRightClick
              }
              errorsAndWarnings={this.props.errorsAndWarnings}
              masterPoolMismatchItems={this.props.masterPoolMismatchItems}
              actionConfigurationName={"Action3"}
            />
            <ConfigVotingInfo
              x={`${zoom * -14}px`}
              y={`${zoom * 133}px`}
              fontSize={`${zoom * 0.9}em`}
              device={
                this.props.devices.filter(
                  device =>
                    device.ptProtectiveFunction.inputs.trim() === "A1O3T"
                )[0]
                }
                ccf={this.props.psfProtectiveFunctionComments.action3CCF}
            />
            <img
              style={{
                zIndex: -1,
                top: `${zoom * 281}px`,
                left: `${zoom * -268}px`,
                width: `${zoom * 324}px`,
                ...horizontalLineStyle
              }}
              src="/HTMLImages/Elements/Horizontal-small1.png"
              />
              <img
                style={{
                  zIndex: -1,
                  top: `${zoom * 281}px`,
                  left: `${zoom * -298}px`,
                  width: `${zoom * 324}px`,
                  ...horizontalLineStyle
                }}
                src="/HTMLImages/Elements/Horizontal-small1.png"
              />
            <img
              style={{
                zIndex: -1,
                top: `${zoom * 120}px`,
                left: `${zoom * -224}px`,
                height: `${zoom * 310}px`,
                ...verticalLineStyle
              }}
              src="/HTMLImages/Elements/Vertical-big.png"
            />
            <img
              style={{
                zIndex: -1,
                top: `${-27 * zoom}px`,
                left: `${zoom * -227}px`,
                width: `${zoom * 492}px`,
                ...horizontalLineStyle
              }}
              src="/HTMLImages/Elements/Horizontal-small1.png"
              />

          </div>
        ) : (
            <EmptyConfiguration
              location="A1O3"
              position={{ top: -63, left: 1616 }}
              size={this.actionConfigSize}
              onRightClick={this.handleEmptyConfigurationRightClicked}
              zoom={this.props.zoom}
            />
          )}

        {/*Action 4*/}
        {this.props.psfProtectiveFunctionComments.action4Config ? (
          <div
            id="diva4"
            style={{
              zIndex: 4,
              position: "absolute",
              top: `${-43 * zoom}px`,
              left: `${2169 * zoom}px`,
              width: `${500 * zoom}px`,
              height: `${400 * zoom}px`,
              overflow: "visible",
              pointerEvents: "none"
            }}
          >
            <ActionConfiguration
              zoom={this.props.zoom}
              location={"A1O4"}
              voting={this.props.psfProtectiveFunctionComments.action4Config}
              devices={this.props.devices.filter(device =>
                device.ptProtectiveFunction.inputs.trim().startsWith("A1O4")
              )}
              handleDeviceClicked={this.handleDeviceClicked}
              onConfigVotingClicked={this.handleConfigVotingClicked}
              handleConfigurationClicked={this.handleConfigurationRightClicked}
              handleDeviceRightClicked={this.handleDeviceRightClicked}
              handleEmptyDeviceLocationClick={
                this.props.handleEmptyDeviceLocationClick
              }
              onEmptyDeviceLocationRightClick={
                this.handleEmptyDeviceLocationRightClick
              }
              errorsAndWarnings={this.props.errorsAndWarnings}
              masterPoolMismatchItems={this.props.masterPoolMismatchItems}
              actionConfigurationName={"Action4"}
            />
            <ConfigVotingInfo
              x={`${zoom * -29}px`}
              y={`${zoom * 125}px`}
              fontSize={`${zoom * 0.9}em`}
              device={
                this.props.devices.filter(
                  device =>
                    device.ptProtectiveFunction.inputs.trim() === "A1O4T"
                )[0]
                }
                ccf={this.props.psfProtectiveFunctionComments.action4CCF}
              />
              <img
                style={{
                  zIndex: -1,
                  top: `${zoom * 275}px`,
                  left: `${zoom * -305}px`,
                  width: `${zoom * 300}px`,
                  ...horizontalLineStyle
                }}
                src="/HTMLImages/Elements/Horizontal-big1.png"
              />
            <img
              style={{
                zIndex: -1,
                top: `${zoom * 275}px`,
                left: `${zoom * -705}px`,
                width: `${zoom * 699}px`,
                ...horizontalLineStyle
              }}
              src="/HTMLImages/Elements/Horizontal-big1.png"
              />
              <img
                style={{
                  zIndex: -1,
                  top: `${275 * zoom}px`,
                  left: `${zoom * -915}px`,
                  width: `${zoom * 464}px`,
                  ...horizontalLineStyle
                }}
                src="/HTMLImages/Elements/Horizontal-small2.png"
              />
            <img
              style={{
                zIndex: -1,
                top: `${zoom * 117}px`,
                left: `${zoom * -179}px`,
                height: `${zoom * 307}px`,
                ...verticalLineStyle
              }}
              src="/HTMLImages/Elements/Vertical-big.png"
            />
            <img
              style={{
                zIndex: -1,
                top: `${-30 * zoom}px`,
                left: `${zoom * -184}px`,
                width: `${zoom * 464}px`,
                ...horizontalLineStyle
              }}
              src="/HTMLImages/Elements/Horizontal-small2.png"
            />
          </div>
        ) : (
            <EmptyConfiguration
              location="A1O4"
              position={{ top: -43, left: 2169 }}
              size={this.actionConfigSize}
              onRightClick={this.handleEmptyConfigurationRightClicked}
              zoom={this.props.zoom}
            />
          )}

        {/*Action 5*/}
        {this.props.psfProtectiveFunctionComments.action5Config ? (
          <div
            id="diva5"
            style={{
              zIndex: 3,
              position: "absolute",
              top: `${279 * zoom}px`,
              left: `${2216 * zoom}px`,
              width: `${500 * zoom}px`,
              height: `${400 * zoom}px`,
              overflow: "visible",
              pointerEvents: "none"
            }}
          >
            <ActionConfiguration
              zoom={this.props.zoom}
              location={"A1O5"}
              voting={this.props.psfProtectiveFunctionComments.action5Config}
              devices={this.props.devices.filter(device =>
                device.ptProtectiveFunction.inputs.trim().startsWith("A1O5")
              )}
              handleDeviceClicked={this.handleDeviceClicked}
              onConfigVotingClicked={this.handleConfigVotingClicked}
              handleConfigurationClicked={this.handleConfigurationRightClicked}
              handleDeviceRightClicked={this.handleDeviceRightClicked}
              handleEmptyDeviceLocationClick={
                this.props.handleEmptyDeviceLocationClick
              }
              onEmptyDeviceLocationRightClick={
                this.handleEmptyDeviceLocationRightClick
              }
              errorsAndWarnings={this.props.errorsAndWarnings}
              masterPoolMismatchItems={this.props.masterPoolMismatchItems}
              actionConfigurationName={"Action5"}
            />
            <ConfigVotingInfo
              x={`${zoom * -68}px`}
              y={`${zoom * 125}px`}
              fontSize={`${zoom * 0.9}em`}
              device={
                this.props.devices.filter(
                  device =>
                    device.ptProtectiveFunction.inputs.trim() === "A1O5T"
                )[0]
                }
                ccf={this.props.psfProtectiveFunctionComments.action5CCF}
              />
              <img
                style={{
                  zIndex: -1,
                  top: `${-32 * zoom}px`,
                  left: `${zoom * -940}px`,
                  width: `${zoom * 400}px`,
                  ...horizontalLineStyle
                }}
                src="/HTMLImages/Elements/Horizontal-small2.png"
              />
              <img
                style={{
                  zIndex: -1,
                  top: `${zoom * -32}px`,
                  left: `${zoom * -300}px`,
                  width: `${zoom * 400}px`,
                  ...horizontalLineStyle
                }}
                src="/HTMLImages/Elements/Horizontal-big1.png"
              />
            <img
              style={{
                zIndex: -1,
                top: `${zoom * -32}px`,
                left: `${zoom * -752}px`,
                width: `${zoom * 850}px`,
                ...horizontalLineStyle
              }}
              src="/HTMLImages/Elements/Horizontal-big1.png"
            />
          </div>
        ) : (
            <EmptyConfiguration
              location="A1O5"
              position={{ top: 279, left: 2216 }}
              size={this.actionConfigSize}
              onRightClick={this.handleEmptyConfigurationRightClicked}
              zoom={this.props.zoom}
            />
          )}

        {/*Support 1*/}
        {this.configHasDevices("SSO1") ||
          this.props.supportConfigPasteState.ss1 ? (
            <div
              id="divs1"
              style={{
                position: "absolute",
                top: `${zoom * 188}px`,
                left: `${zoom * 1258}px`,
                width: `${zoom * 100}px`,
                height: `${zoom * 100}px`,
                display: "block"
              }}
            >
              <SupportConfiguration
                  zoom={this.props.zoom}
                  device={
                    this.props.devices.filter(
                      device => device.ptProtectiveFunction.inputs.trim() === "SSO1"
                    )[0]
                  }
                  handleDeviceClicked={this.handleDeviceClicked}
                  handleDeviceRightClicked={this.handleDeviceRightClicked}
                  handleEmptySupportDeviceLocationClick={() =>
                    this.props.handleEmptySupportDeviceLocationClick(1)
                  }
                  errorsAndWarnings={this.props.errorsAndWarnings}
                  masterPoolMismatchItems={this.props.masterPoolMismatchItems}
                  supportConfigurationName="Support1"
                  handleConfigurationRightClick={e => this.handleConfigurationRightClicked(e, "SSO1")}
              />
            </div>
          ) : (
            <div
              id="divs1"
              style={{
                position: "absolute",
                top: `${zoom * 188}px`,
                left: `${zoom * 1258}px`,
                width: `${zoom * 100}px`,
                height: `${zoom * 100}px`,
                display: "block"
              }}
            />
          )}

        {/*Support 2*/}
        {this.configHasDevices("SSO2") ||
          this.props.supportConfigPasteState.ss2 ? (
            <div
              id="divs2"
              style={{
                position: "absolute",
                top: `${zoom * 188}px`,
                left: `${zoom * 1357}px`,
                width: `${zoom * 100}px`,
                height: `${zoom * 100}px`
              }}
            >
              <SupportConfiguration
                zoom={this.props.zoom}
                device={
                  this.props.devices.filter(
                    device => device.ptProtectiveFunction.inputs.trim() === "SSO2"
                  )[0]
                }
                handleDeviceClicked={this.handleDeviceClicked}
                handleDeviceRightClicked={this.handleDeviceRightClicked}
                handleEmptySupportDeviceLocationClick={() =>
                  this.props.handleEmptySupportDeviceLocationClick(2)
                }
                errorsAndWarnings={this.props.errorsAndWarnings}
                masterPoolMismatchItems={this.props.masterPoolMismatchItems}
                supportConfigurationName="Support2"
                  handleConfigurationRightClick={e => this.handleConfigurationRightClicked(e, "SSO2")}
              />
            </div>
          ) : (
            <div
              id="divs2"
              style={{
                position: "absolute",
                top: `${zoom * 188}px`,
                left: `${zoom * 1357}px`,
                width: `${zoom * 100}px`,
                height: `${zoom * 100}px`
              }}
            />
          )}
        {/*Manual Input*/}
        {this.configHasDevices("INP6") ? (
          <div
            id="manual_input"
            style={{
              zIndex: 5,
              position: "absolute",
              top: `${zoom * 514}px`,
              left: `${zoom * 1258}px`,
              width: `${zoom * 100}px`,
              height: `${zoom * 100}px`,
              display: "block"
            }}
          >
            <ManualInputConfiguration
              zoom={this.props.zoom}
              devices={this.props.devices.filter(
                devie => devie.ptProtectiveFunction.inputs.trim() === "INP6"
              )}
              handleDeviceClicked={this.handleDeviceClicked}
              handleManualInputDeviceRightClicked={
                this.handleManualInputDeviceRightClicked
              }
              errorsAndWarnings={this.props.errorsAndWarnings}
            />
          </div>
        ) : (
            <div
              id="manual_input"
              style={{
                position: "absolute",
                top: `${zoom * 514}px`,
                left: `${zoom * 1258}px`,
                width: `${zoom * 100}px`,
                height: `${zoom * 100}px`,
                display: "block"
              }}
            />
          )}
        {/*Manual Action*/}
        {this.configHasDevices("A1O6") ? (
          <div
            id="manual_action"
            style={{
              zIndex: 5,
              position: "absolute",
              top: `${zoom * 514}px`,
              left: `${zoom * 1357}px`,
              width: `${zoom * 100}px`,
              height: `${zoom * 100}px`
            }}
          >
            <ManualActionConfiguration
              zoom={this.props.zoom}
              devices={this.props.devices.filter(
                devie => devie.ptProtectiveFunction.inputs.trim() === "A1O6"
              )}
              handleDeviceClicked={this.handleDeviceClicked}
              handleManualActionDeviceRightClicked={
                this.handleManualActionDeviceRightClicked
              }
              errorsAndWarnings={this.props.errorsAndWarnings}
            />
          </div>
        ) : (
            <div
              id="manual_action"
              style={{
                position: "absolute",
                top: `${zoom * 514}px`,
                left: `${zoom * 1357}px`,
                width: `${zoom * 100}px`,
                height: `${zoom * 100}px`
              }}
            />
          )}
        {/*Modal*/}
        {this.state.shownModal === "deviceModal" && this.state.dataSheetInfo ? (
          <DeviceModal
              device={
                this.props.devices.filter(
                  device =>
                    device.ptProtectiveFunction.protectiveFunctionID ===
                    this.state.selectedDeviceId
                )[0]
              }
              closeModal={this.closeModal}
              dataSheetInfo={this.state.dataSheetInfo}
              onDeviceUpdateClick={this.props.onUpdateDeviceSheetData}
              onDeviceSheetRestoreClick={this.props.onRestoreDeviceSheetData}
              masterPoolMismatchItems={this.props.masterPoolMismatchItems}
              readOnly={this.props.readOnly}
          />
        ) : null}
        {this.state.shownModal === "supportModal" &&
          this.state.supportSystemSheet ? (
            <SupportModal
                supportSystem={this.props.devices.find(
                  device =>
                    device.ptProtectiveFunction.protectiveFunctionID ===
                    this.state.selectedSupportDeviceId
                )}
                closeModal={this.closeModal}
                supportSystemSheet={this.state.supportSystemSheet}
                setDiagramData={this.props.setDiagramData}
                onDeviceSheetRestoreClick={this.props.onRestoreDeviceSheetData}
                masterPoolMismatchItems={this.props.masterPoolMismatchItems}
                readOnly={this.props.readOnly}
            />
          ) : null}
        {this.state.shownModal === "logicSolverModal" ? (
          <LogicModal
              device={
                this.props.devices.filter(
                  device =>
                    device.ptProtectiveFunction.protectiveFunctionID ===
                    this.state.selectedLogicSolverId
                )[0]
              }
              closeModal={this.closeModal}
              onUpdateLogicSolverDataClick={
                this.props.handleUpdateLogicSolverDataClick
              }
              onDeviceSheetRestoreClick={this.props.onRestoreDeviceSheetData}
              masterPoolMismatchItems={this.props.masterPoolMismatchItems}
              readOnly={this.props.readOnly}
          />
        ) : null}
        {this.state.shownModal === "configVotingModal" ? (
          <CcfSpecModal
            psfProtectiveFunctionComments={
              this.props.psfProtectiveFunctionComments
            }
            location={this.state.clickedLocation}
            onCloseModal={this.closeModal}
            onSubmit={this.props.onUpdateCcf}
            zoom={this.props.zoom}
          />
        ) : null}
        {this.state.shownModal === "MooNConfigModal" ? (
          <MooNInputConfigModal
            psfProtectiveFunctionComments={
              this.props.psfProtectiveFunctionComments
            }
            location={this.state.clickedLocation}
            onCloseModal={this.closeModal}
            onSubmit={this.props.onUpdateCcf}
            zoom={this.props.zoom}
          />
        ) : null}
        {this.state.shownModal === "noCcfModal" ? (
          <NoCcfModal
            zoom={this.props.zoom}
            devices={this.props.devices.filter(
              device =>
                device.ptProtectiveFunction.inputs.trim() === this.state.inputs
            )}
            closeModal={this.closeModal}
          />
        ) : null}
        {this.state.shownModal === "inputRedundancyModal" ? (
          <InputRedundancyModal
            zoom={this.props.zoom}
            devices={this.props.devices.filter(
              device =>
                device.ptProtectiveFunction.inputs.trim() === this.state.inputs
            )}
            closeModal={this.closeModal}
            onInputRedundancyChange={this.props.onInputRedundancyChange}
            redundancy={redundancy}
          />
        ) : null}
        {this.state.shownModal === "manualInputModal" ? (
          <ManualInputModal
            closeModal={this.closeModal}
            device={
              this.props.devices.filter(
                device =>
                  device.ptProtectiveFunction.protectiveFunctionID ===
                  this.state.selectedDeviceId
              )[0]
            }
            manualInputs={this.props.manualInputs}
            onManualInputsSave={this.props.onManualInputsSave}
            saveManualInputsFailed={this.saveManualInputsFailed}
          />
        ) : null}
        {this.state.shownModal === "manualActionModal" ? (
          <ManualActionModal
            closeModal={this.closeModal}
            device={
              this.props.devices.filter(
                device =>
                  device.ptProtectiveFunction.protectiveFunctionID ===
                  this.state.selectedDeviceId
              )[0]
            }
            manualAction={this.props.manualAction}
            onManualActionSave={this.props.onManualActionSave}
            saveManualActionsFailed={this.saveManualActionsFailed}
          />
        ) : null}

        {this.props.showErrorsAndWarningsModal ? (
          <CalculationResultModal
            messages={this.props.errorsAndWarnings}
            onClose={() => this.props.acceptCalculationResult()}
          />
        ) : null}
        {this.state.shownModal === "SaveManInputsFailed" ? (
          <SaveFailedModal
            message="Save manual inputs failed"
            onClose={this.closeModal}
          />
        ) : null}
        {this.state.shownModal === "SaveManActionsFailed" ? (
          <SaveFailedModal
            message="Save manual actions failed"
            onClose={this.closeModal}
          />
        ) : null}
      </div>
        </InteractiveDiv >
    );
  }
}
